import axios from 'axios';
import { API_BASE_URL } from '../config';

// Create axios instance
const api = axios.create({
    baseURL: `${API_BASE_URL}/api/v1`,
    timeout: 50000,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Request interceptor to add auth token and user switching headers
api.interceptors.request.use(
    (config) => {
        // Add auth token
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Add user switching headers if switched user exists
        const switchedUser = localStorage.getItem('switchedUser');
        const originalUser = localStorage.getItem('originalUser');

        if (switchedUser && originalUser) {
            const switchedUserData = JSON.parse(switchedUser);
            const originalUserData = JSON.parse(originalUser);

            config.headers['x-switched-user-id'] = switchedUserData.id;
            config.headers['x-original-user-id'] = originalUserData.id;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const refreshToken = localStorage.getItem('refreshToken');
                if (!refreshToken) {
                    throw new Error('No refresh token');
                }

                // Используйте правильный URL с префиксом /api/v1
                const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh-token`, {
                    refreshToken: refreshToken
                });

                const { accessToken, refreshToken: newRefreshToken } = response.data;

                localStorage.setItem('accessToken', accessToken);
                localStorage.setItem('refreshToken', newRefreshToken);

                // Update the authorization header and retry the request
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                return api(originalRequest);

            } catch (refreshError) {
                console.error('Authentication error:', refreshError);
                // Clear tokens and redirect to login
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('originalUser');
                localStorage.removeItem('switchedUser');

                // Only redirect if we're not already on the login page
                if (window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

// Campaign analysis tasks API
export const getAnalysisTasks = (status) => {
  const params = status ? `?status=${encodeURIComponent(status)}` : '';
  return api.get(`/analysis-tasks${params}`);
};

export const getAnalysisTask = (id) => api.get(`/analysis-tasks/${id}`);
export const createAnalysisTask = (data) => api.post('/analysis-tasks', data);
export const deleteAnalysisTask = (id) => api.delete(`/analysis-tasks/${id}`);
export const previewAnalysisTask = (data) => api.post('/analysis-tasks/preview', data);
export const startAnalysisTask = (id) => api.post(`/analysis-tasks/${id}/start`);
export const getAnalysisTaskStatus = (id) => api.get(`/analysis-tasks/${id}/status`);
export const getAnalysisTaskResults = (id) => api.get(`/analysis-tasks/${id}/results`);
export const downloadNoReplyNumbers = (id) => api.get(`/analysis-tasks/${id}/no-reply/download`, { responseType: 'blob' });

// Completed campaigns for analysis
export const getCompletedCampaigns = () => api.get('/campaigns?status=completed');

// Chat messages
export const getChatMessages = (chatId, params = {}) => api.get(`/chats/${chatId}/messages`, { params });

export default api;
